import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Dialog,
  IconButton,
  Link,
  Skeleton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';

import { ReactComponent as CookiesIcon } from '../../../assets/icons/agreements/cookies.svg';
import { AgreementType } from '../../../redux/api/agreements/interfaces';
import {
  AppButton,
  AppCancelButton,
  AppSaveButton,
} from '../../designe/buttons/app-button/AppButton';
import AgreementsContent from './AgreementsContent';
import { useStyles } from './AgreementsModal.styles';
import { useAgreementContent } from './useAgreementContent';

export enum AgreementsModalMode {
  FORCED = 'forced',
  VIEW = 'view',
}

function ViewModeCloseButton({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <IconButton
      aria-label={t('common.close')}
      onClick={onClose}
      size="small"
      sx={{ color: 'text.secondary' }}>
      <CloseIcon />
    </IconButton>
  );
}

interface IAgreementsModalProps {
  open: boolean;
  mode: AgreementsModalMode;
  agreementType: AgreementType;
  compactCookieForcedMode?: boolean;
  isSubmitting?: boolean;
  onAgree?: () => Promise<void> | void;
  onClose: () => void;
}

const AgreementsModal = ({
  open,
  mode,
  agreementType,
  compactCookieForcedMode = true,
  isSubmitting = false,
  onAgree,
  onClose,
}: IAgreementsModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { classes } = useStyles(theme);
  const isCookieForcedMode =
    mode === AgreementsModalMode.FORCED &&
    agreementType === AgreementType.COOKIE_POLICY &&
    compactCookieForcedMode;
  const {
    data: blocks = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useAgreementContent(agreementType, open && !isCookieForcedMode);
  const [viewAgreementType, setViewAgreementType] =
    useState<AgreementType | null>(null);

  const canClose = mode === AgreementsModalMode.VIEW;
  const isContentModal = !isCookieForcedMode;
  const isContentLoading = isLoading || isFetching;
  const isAgreeDisabled =
    isSubmitting || (!isCookieForcedMode && (isLoading || isFetching));

  useEffect(() => {
    if (!open) {
      return;
    }
    setViewAgreementType(null);
  }, [open, mode, agreementType]);

  const handleDialogClose = (
    _: unknown,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (
      !canClose &&
      (reason === 'backdropClick' || reason === 'escapeKeyDown')
    ) {
      return;
    }
    if (canClose) {
      onClose();
    }
  };

  const handleAgree = async () => {
    if (mode !== AgreementsModalMode.FORCED || !onAgree) {
      return;
    }
    await onAgree();
  };

  return (
    <Dialog
      open={open}
      fullWidth
      fullScreen={isContentModal && isMobile}
      maxWidth={false}
      onClose={handleDialogClose}
      disableEscapeKeyDown={!canClose}
      classes={{
        paper: isCookieForcedMode ? classes.cookiePaper : classes.dialogPaper,
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: isCookieForcedMode ? 'flex-end' : 'center',
        },
      }}>
      <div className={classes.content}>
        {canClose && isContentModal && isContentLoading && (
          <Box className={classes.viewToolbarClose}>
            <ViewModeCloseButton onClose={onClose} />
          </Box>
        )}
        {isCookieForcedMode ? (
          <div className={classes.cookieHeader}>
            <div className={classes.cookieIconWrap}>
              <CookiesIcon className={classes.cookieIcon} />
            </div>
            <div className={classes.cookieDescription}>
              {t('agreements.cookieConsentDescriptionPrefix')}{' '}
              <Link
                href="#cookie-policy"
                className={classes.inlineLink}
                onClick={(event) => {
                  event.preventDefault();
                  setViewAgreementType(AgreementType.COOKIE_POLICY);
                }}>
                {t('agreements.cookiePolicyTitle')}
              </Link>{' '}
              {t('agreements.and')}{' '}
              <Link
                href="#privacy-policy"
                className={classes.inlineLink}
                onClick={(event) => {
                  event.preventDefault();
                  setViewAgreementType(AgreementType.PRIVACY_POLICY);
                }}>
                {t('agreements.privacyPolicyTitle')}
              </Link>
              .
            </div>
          </div>
        ) : isContentLoading ? (
          <Box display="flex" flexDirection="column" gap={1.2}>
            <Skeleton variant="text" width="65%" height={34} />
            <Skeleton variant="text" width="100%" height={28} />
            <Skeleton variant="text" width="96%" height={28} />
            <Skeleton variant="text" width="88%" height={28} />
          </Box>
        ) : null}
        {!isCookieForcedMode && !isContentLoading && isError && (
          <>
            {canClose && (
              <Box className={classes.viewToolbarClose}>
                <ViewModeCloseButton onClose={onClose} />
              </Box>
            )}
            <Box display="flex" justifyContent="center">
              <AppButton
                variant="outlined"
                color="primary"
                icon={<RefreshIcon sx={{ fontSize: 20 }} />}
                onClick={() => refetch()}>
                {t('common.retry')}
              </AppButton>
            </Box>
          </>
        )}
        {!isCookieForcedMode && !isContentLoading && !isError && (
          <AgreementsContent
            blocks={blocks}
            agreementType={agreementType}
            headerClose={canClose ? onClose : undefined}
          />
        )}
      </div>
      <div className={classes.actions}>
        {canClose ? (
          <AppCancelButton
            onClick={onClose}
            className={isMobile ? classes.mobilePrimaryAction : undefined}>
            {t('common.close')}
          </AppCancelButton>
        ) : isCookieForcedMode ? (
          <>
            <AppButton
              variant="contained"
              color="primary"
              onClick={handleAgree}
              disabled={isAgreeDisabled}>
              {t('agreements.acceptAll')}
            </AppButton>
            <AppButton
              variant="outlined"
              color="primary"
              onClick={handleAgree}
              disabled={isAgreeDisabled}>
              {t('agreements.acceptNecessaryOnly')}
            </AppButton>
          </>
        ) : (
          <AppSaveButton
            onClick={handleAgree}
            disabled={isAgreeDisabled}
            className={isMobile ? classes.mobilePrimaryAction : undefined}>
            {t('agreements.agree')}
          </AppSaveButton>
        )}
      </div>
      {viewAgreementType && (
        <AgreementsModal
          open={true}
          mode={AgreementsModalMode.VIEW}
          agreementType={viewAgreementType}
          onClose={() => setViewAgreementType(null)}
        />
      )}
    </Dialog>
  );
};

export default AgreementsModal;
